import { useRef, useCallback, useEffect, useState } from 'react';

/**
 * Hook to enable click-and-drag scrolling for horizontal scrollable containers
 * Only works on desktop (non-touch devices)
 * 
 * @param enabled - Whether drag-to-scroll is enabled (default: true)
 * @param existingRef - Optional existing ref to use instead of creating a new one
 * @returns Object with ref to attach to the scrollable container and event handlers
 */
export function useDragToScroll(enabled: boolean = true, existingRef?: React.RefObject<HTMLDivElement | null>) {
  const internalRef = useRef<HTMLDivElement | null>(null);
  const containerRef = existingRef || internalRef;
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const [isTouchDevice, setIsTouchDevice] = useState(() => {
    if (typeof window === 'undefined') return false;
    return (
      'ontouchstart' in window || 
      navigator.maxTouchPoints > 0 ||
      // @ts-ignore - some browsers have this
      (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0)
    );
  });

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    
    const container = containerRef.current;
    if (!container) return;

    e.preventDefault();
    
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startXRef.current) * 2; // Multiply by 2 for faster scrolling
    container.scrollLeft = scrollLeftRef.current - walk;
  }, [containerRef]);

  const handleMouseUp = useCallback(() => {
    if (!isDraggingRef.current) return;
    
    isDraggingRef.current = false;
    
    const container = containerRef.current;
    if (container) {
      container.style.cursor = '';
      container.style.userSelect = '';
    }
    
    // Restore body styles
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, [containerRef]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Only enable on desktop (non-touch devices)
    if (!enabled || isTouchDevice) return;
    
    // Only start drag on left mouse button
    if (e.button !== 0) return;

    const container = containerRef.current;
    if (!container) return;

    // Prevent default to avoid text selection
    e.preventDefault();
    
    isDraggingRef.current = true;
    startXRef.current = e.pageX - container.offsetLeft;
    scrollLeftRef.current = container.scrollLeft;
    
    // Change cursor to indicate dragging
    container.style.cursor = 'grabbing';
    container.style.userSelect = 'none';
    
    // Add class to body to prevent text selection globally
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    
    // Add global event listeners for mouse move and up
    const mouseMoveHandler = (e: MouseEvent) => handleMouseMove(e);
    const mouseUpHandler = () => {
      handleMouseUp();
      window.removeEventListener('mousemove', mouseMoveHandler);
      window.removeEventListener('mouseup', mouseUpHandler);
    };
    
    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('mouseup', mouseUpHandler);
  }, [enabled, isTouchDevice, handleMouseMove, handleMouseUp]);

  const handleMouseLeave = useCallback(() => {
    // Stop dragging if mouse leaves the container
    if (isDraggingRef.current) {
      handleMouseUp();
    }
  }, [handleMouseUp]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isDraggingRef.current) {
        handleMouseUp();
      }
    };
  }, [handleMouseUp]);

  return {
    ref: existingRef ? undefined : containerRef,
    onMouseDown: handleMouseDown,
    onMouseLeave: handleMouseLeave,
    // Add cursor style when hovering (only on desktop)
    style: enabled && !isTouchDevice 
      ? { cursor: 'grab' } as React.CSSProperties
      : undefined,
  };
}
